import { useEffect, useState } from 'react';

import { Switch } from 'components/forms';
import { toasterService } from 'components/feedback/Toaster/toasterService';
import { usePanel } from 'hooks/usePanel';
import { DisplayTypeEnum } from 'api/files';
import { GenericColors } from 'api/tenant';

import {
  GetCollaboratorBriquesResponse,
  getCollaboratorBriques,
  setCollaboratorBriques,
} from '../../../api/personneMoraleAccessApi';

interface MemberBriquesFormProps {
  accessId: string;
  memberName: string;
  onSuccess: () => void;
}

/**
 * Panel « briques du membre » — la redistribution des rôles au sein du cabinet :
 * l'Owner active/désactive chaque brique du profil de son cabinet pour CE membre.
 * Le plafond (briques attribuables) vient du serveur ; une brique posée par un
 * admin hors profil n'apparaît pas et n'est jamais touchée. L'auto-édition est
 * refusée côté serveur — l'entrée de menu est déjà cachée pour soi-même.
 */
export const MemberBriquesForm: React.FC<MemberBriquesFormProps> = ({
  accessId,
  memberName,
  onSuccess,
}) => {
  const { setPanelActions, closePanel } = usePanel();

  const [etat, setEtat] = useState<GetCollaboratorBriquesResponse | null>(null);
  const [briques, setBriques] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getCollaboratorBriques(accessId)
      .then((res) => {
        if (cancelled) return;
        setEtat(res);
        const attribuables = res.briquesAttribuables.map((b) => b.nom);
        setBriques(res.briquesCompte.filter((b) => attribuables.includes(b)));
      })
      .catch(() => {
        if (!cancelled) setError('Impossible de charger les briques de ce membre.');
      });
    return () => {
      cancelled = true;
    };
  }, [accessId]);

  useEffect(() => {
    setPanelActions({
      closeLabel: 'COMMON.CANCEL',
      submitLabel: 'COMMON.VALIDATE',
      submitDisabled: isSaving || !etat,
      onSubmit: async () => {
        if (!etat) return;
        setIsSaving(true);
        try {
          const res = await setCollaboratorBriques(accessId, briques);
          if (!res.isSuccess) {
            setError(res.errorMessage ?? 'Échec de la mise à jour des briques.');
            return;
          }
          toasterService.showToast({
            message: `Briques de ${memberName} mises à jour.`,
            displayType: DisplayTypeEnum.Toaster,
            color: GenericColors.Green,
          });
          onSuccess();
          closePanel();
        } catch {
          // Axios jette sur les 4xx/5xx et le réseau : sans ce catch, le rejet
          // partait non géré et le panneau restait muet.
          setError('Échec de la mise à jour des briques.');
        } finally {
          setIsSaving(false);
        }
      },
    });
  }, [setPanelActions, closePanel, onSuccess, accessId, memberName, etat, briques, isSaving]);

  if (!etat) {
    return error ? (
      <p className="text-sm text-red-600">{error}</p>
    ) : (
      <p className="text-sm text-gray-500">Chargement…</p>
    );
  }

  const basculer = (brique: string, active: boolean) =>
    setBriques((prev) => (active ? [...prev, brique] : prev.filter((b) => b !== brique)));

  return (
    <div className="space-y-5">
      <div>
        <div className="mb-2 flex items-baseline gap-2">
          <h4 className="text-sm font-semibold text-gray-900">Briques métier</h4>
          <span className="text-xs tabular-nums text-gray-400">
            {briques.length} activée{briques.length > 1 ? 's' : ''} sur{' '}
            {etat.briquesAttribuables.length}
          </span>
        </div>

        <div className="divide-y overflow-hidden rounded-xl border">
          {etat.briquesAttribuables.map(({ nom, description }) => (
            <div key={nom} className="flex items-center gap-3 bg-white px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900">{nom}</p>
                {description && <p className="text-xs text-gray-500">{description}</p>}
              </div>
              <Switch
                name={`brique-${nom}`}
                checked={briques.includes(nom)}
                onChange={(checked) => basculer(nom, checked)}
              />
            </div>
          ))}
        </div>

        {briques.length === 0 && (
          <p className="mt-2 text-xs text-amber-600">
            Aucune brique : ce membre n'aura aucun droit métier.
          </p>
        )}
      </div>

      {(etat.droitsSpecifiquesPlateformes?.length ?? 0) > 0 && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Un administrateur a posé des droits spécifiques pour ce membre sur :{' '}
          {etat.droitsSpecifiquesPlateformes!.map((p) => p.designation).join(', ')}. Ces
          droits priment — vos modifications n'auront pas d'effet sur ces plateformes.
        </p>
      )}

      <p className="text-xs text-gray-500">
        Vous distribuez ici les briques du profil de votre cabinet, membre par membre. Une
        brique accordée par un administrateur en dehors de ce profil n'apparaît pas et n'est
        pas modifiée. Effet à la prochaine requête du compte.
      </p>

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};
